// src/app/services/agents-api.ts
import { apiFetch, getStoredToken, notifyAuthExpired, AuthExpiredError } from "@/api/api-fetch";
import { buildWsUrl } from "@/api/url-route";

// Build query string helper — skips undefined/null/empty values
function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const pairs: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    pairs.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return pairs.length ? `?${pairs.join("&")}` : "";
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AgentRunOptions {
  perception_score: boolean;
  detailed_analysis: boolean;
  enable_download: boolean;
  enable_email: boolean;
  email_address?: string;
}

export interface Agent {
  id: number;
  username: string;
  name: string;
  description: string;
  prompt: string;
  brand_name: string;
  selected_models: string[];
  run_options: AgentRunOptions;
  tags: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AgentRunSummary {
  id: number;
  agent_id: number | null;
  agent_name: string;
  brand_name: string;
  models_used: string[];
  perception_score: number | null;
  status: string;
  created_at: string;
}

export interface AgentRecommendation {
  title: string;
  description: string;
  priority: "HIGH" | "MEDIUM" | "LOW";
  impact: number;
}

export interface AgentModelResult {
  model: string;
  perception_score: number;
  sentiment: string;
  confidence: number;
  summary: string;
  key_findings: string[];
  strengths: string[];
  weaknesses: string[];
  recommendations: AgentRecommendation[];
  raw_analysis: string;
  data_sources: string[];
  error?: string;
}

export interface AgentAggregatedResult {
  overall_perception_score: number;
  sentiment: string;
  consensus_summary: string;
  key_findings: string[];
  combined_strengths: string[];
  combined_weaknesses: string[];
  top_recommendations: AgentRecommendation[];
  model_scores: Record<string, number>;
  models_count: number;
  brand_name: string;
}

export interface AgentRunDetail {
  id: number;
  agent_id: number | null;
  agent_name: string;
  brand_name: string;
  prompt_used: string;
  models_used: string[];
  run_options: AgentRunOptions;
  per_model_results: Record<string, AgentModelResult>;
  aggregated_results: AgentAggregatedResult;
  perception_score: number | null;
  status: string;
  created_at: string;
}

// ─── REST calls ───────────────────────────────────────────────────────────────

export const agentsApi = {
  /**
   * List workflows. Optional `brandName` filters by brand (case-insensitive);
   * workflows with no brand_name are always shown as global.
   * Back-compat: callable with no args (returns all workflows).
   */
  list: (opts?: { brandName?: string; includeInactive?: boolean }) =>
    apiFetch<{ items: Agent[] }>(
      `/agents${qs({ brand_name: opts?.brandName, include_inactive: opts?.includeInactive })}`
    ).then((r) => r.items),

  create: (body: Omit<Agent, "id" | "username" | "is_active" | "created_at" | "updated_at">) =>
    apiFetch<{ id: number; created_at: string }>("/agents", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  get: (id: number) => apiFetch<Agent>(`/agents/${id}`),

  update: (
    id: number,
    body: Partial<Omit<Agent, "id" | "username" | "created_at" | "updated_at">>,
  ) => apiFetch<{ ok: boolean }>(`/agents/${id}`, { method: "PUT", body: JSON.stringify(body) }),

  delete: (id: number) =>
    apiFetch<{ ok: boolean }>(`/agents/${id}`, { method: "DELETE" }),

  runs: (agentId: number, limit = 50) =>
    apiFetch<{ items: AgentRunSummary[] }>(`/agents/${agentId}/runs?limit=${limit}`).then(
      (r) => r.items,
    ),

  /**
   * List workflow runs across all agents.
   *
   * Back-compat overload:
   *   - allRuns()          → all runs, default limit 50
   *   - allRuns(100)       → all runs, limit 100       (legacy call signature)
   *   - allRuns({ limit, brandName })                  (new call signature)
   */
  allRuns: (optsOrLimit?: number | { limit?: number; brandName?: string }) => {
    const opts = typeof optsOrLimit === "number"
      ? { limit: optsOrLimit }
      : (optsOrLimit || {});
    const limit = opts.limit ?? 50;
    return apiFetch<{ items: AgentRunSummary[] }>(
      `/agent-runs${qs({ limit, brand_name: opts.brandName })}`
    ).then((r) => r.items);
  },

  getRun: (runId: number) => apiFetch<AgentRunDetail>(`/agent-runs/${runId}`),
};

// ─── WebSocket runner ─────────────────────────────────────────────────────────

export type AgentWsEvent =
  | { type: "started"; brand: string; totalModels: number; models: string[] }
  | { type: "model_status"; engine: string; status: "running" | "done" | "error"; message?: string }
  | { type: "progress"; done: number; total: number; percent: number }
  | { type: "final_result"; run_id: number; data: { per_model_results: Record<string, AgentModelResult>; aggregated: AgentAggregatedResult }; errors: Record<string, string>; email_status?: string; email_error?: string | null; email_to?: string | null }
  | { type: "error"; message: string; errors?: Record<string, string> };

export interface RunAgentParams {
  agent_id?: number;
  agent_name?: string;
  brand_name: string;
  prompt: string;
  models: string[];
  run_options?: Partial<AgentRunOptions>;
}

export function openAgentRunSocket(
  params: RunAgentParams,
  onEvent: (evt: AgentWsEvent) => void,
  onError: () => void,
  onClose: () => void,
): WebSocket {
  const token = getStoredToken();
  if (!token) {
    notifyAuthExpired("/ws/agent-run");
    throw new AuthExpiredError("/ws/agent-run");
  }
  const base = buildWsUrl();
  const ws = new WebSocket(`${base}/ws/agent-run?token=${encodeURIComponent(token)}`);

  ws.onopen = () => ws.send(JSON.stringify(params));
  ws.onmessage = (e) => {
    try {
      onEvent(JSON.parse(e.data));
    } catch {}
  };
  ws.onerror = onError;
  ws.onclose = onClose;
  return ws;
}